<script setup>
import { showModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, watch } from 'vue';
import { useNotification } from "@kyvg/vue3-notification"
import _ from 'lodash';
import moment from 'moment';
import { useStore } from 'vuex';
import Spinner from "@/components/ui/Spinner.vue";
import PaywallService from '@/services/PaywallService';
import PaywallTransactionDetailsModal from '@/components/modals/PaywallTransactionDetailsModal.vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconSearch from '@/components/icon/IconSearch.vue';
import IconCross from '@/components/icon/IconCross.vue';
import IconInfo from '@/components/icon/IconInfo.vue';
import IconRefund from '@/components/icon/IconRefund.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import IconInboxEmpty from '@/components/icon/IconInboxEmpty.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue';

const { notify } = useNotification()
const store = useStore()

// Props
const props = defineProps({
	paywallStreamId: {
		type: Number,
		require: false
	}
})

// Refs
const transactions = ref([])
const loading = ref(false)
const selectedTxn = ref(null)
const refundingId = ref(null)
const search = ref('')

// Status display + color mapping (handles raw API values and formatted labels)
const STATUS_META = {
	active: { label: 'Succeeded', text: 'text-success', bg: 'bg-success-transparent', dot: 'bg-success' },
	succeeded: { label: 'Succeeded', text: 'text-success', bg: 'bg-success-transparent', dot: 'bg-success' },
	pending: { label: 'Pending', text: 'text-main-light', bg: 'bg-main-transparent', dot: 'bg-main' },
	expired: { label: 'Expired', text: 'text-surface-7', bg: 'bg-surface-3', dot: 'bg-surface-6' },
	refund: { label: 'Refunded', text: 'text-error', bg: 'bg-error-transparent', dot: 'bg-error' },
	refunded: { label: 'Refunded', text: 'text-error', bg: 'bg-error-transparent', dot: 'bg-error' },
}
const statusMeta = (status) => STATUS_META[String(status || '').toLowerCase()] || {
	label: status || 'Unknown', text: 'text-surface-7', bg: 'bg-surface-3', dot: 'bg-surface-6'
}

// Computed
const currentPaywall = computed(() => store.getters['Paywalls/currentPaywall'])

const scopedTransactions = computed(() => {
	if (!props.paywallStreamId) return transactions.value
	return _.filter(transactions.value, { stream_id: props.paywallStreamId })
})

const filteredTransactions = computed(() => {
	const query = search.value.trim().toLowerCase()
	if (!query) return scopedTransactions.value
	return scopedTransactions.value.filter((t) =>
		String(t.customer || '').toLowerCase().includes(query)
	)
})

const hasTransactions = computed(() => scopedTransactions.value.length > 0)

// Client-side pagination (the full set is already loaded, so search + export
// keep working across everything).
const PAGE_SIZE = 25
const page = ref(1)
const totalPages = computed(() => Math.max(1, Math.ceil(filteredTransactions.value.length / PAGE_SIZE)))
const pagedTransactions = computed(() => {
	const start = (page.value - 1) * PAGE_SIZE
	return filteredTransactions.value.slice(start, start + PAGE_SIZE)
})
const rangeStart = computed(() => (filteredTransactions.value.length ? (page.value - 1) * PAGE_SIZE + 1 : 0))
const rangeEnd = computed(() => Math.min(page.value * PAGE_SIZE, filteredTransactions.value.length))
// Reset to page 1 when the result set changes; clamp if it shrinks.
watch(() => [search.value, scopedTransactions.value.length], () => { page.value = 1 })
watch(totalPages, (tp) => { if (page.value > tp) page.value = tp })

const canRefund = (row) => row.amount != '0.00' && statusMeta(row.status).label === 'Succeeded'

// Subtle zebra striping for easier row scanning
const zebraBg = (idx) => idx % 2 === 0 ? 'bg-surface-2/60' : 'bg-surface-2/25'

// Lifecycle
onMounted(async () => {
	await setupTransactions();
})

// Methods
const clearSearch = () => { search.value = '' }

const openTransactionDetailsModal = (row) => {
	selectedTxn.value = _.find(transactions.value, { id: row.id })?.txn
	setTimeout(() => {
		showModal('modal-paywall-transaction-details');
	}, 100);
}

const refundPayment = async (row) => {
	const idx = _.findIndex(transactions.value, { id: row.id })
	if (idx === -1) return
	selectedTxn.value = transactions.value[idx].txn
	refundingId.value = row.id;

	try {
		await PaywallService.refundPayment(selectedTxn.value.payments[0].id);

		selectedTxn.value.status = "refund";
		if (selectedTxn.value.payments.length) {
			selectedTxn.value.payments[0].status = "refund";
		}
		transactions.value[idx].status = 'refund'

		notify({ group: "success", text: "Payment refunded" });
	} catch (err) {
		notify({ group: "error", text: err.message || "Couldn't refund payment" });
		console.error("refund payment failed", err);
	}

	refundingId.value = null;
}

const setupTransactions = async () => {
	loading.value = true;
	try {
		const txns = await PaywallService.getTransactions();
		transactions.value = _.map(txns, (txn) => {
			return {
				id: txn.id,
				date: moment(new Date(txn.created_at)).format('MMM DD, YYYY · hh:mm A'),
				stream_id: txn.stream_id,
				customer: txn.user?.email || 'n/a',
				currency: txn.currency,
				amount: txn.price,
				status: txn.status,
				txn: txn
			}
		}).reverse();
	} catch (err) {
		notify({ group: 'error', text: err.message || "Couldn't load transactions" })
		console.error('getTransactions failed', err);
	}
	loading.value = false;
}

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`

const onBtnExport = () => {
	if (!filteredTransactions.value.length) return
	const header = ['ID', 'Date', 'Customer', 'Currency', 'Amount', 'Status']
	const rows = filteredTransactions.value.map((t) => [
		t.id, t.date, t.customer, String(t.currency || '').toUpperCase(), t.amount, statusMeta(t.status).label
	])
	const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n')
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = `${currentPaywall.value?.title || 'paywall'}-transactions.csv`
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}
</script>

<template>
	<section class="bg-surface-1 rounded-xl p-4 sm:p-5">
		<!-- Header -->
		<div class="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
			<div class="min-w-0 grow">
				<h3 class="text-sm font-semibold text-white mb-0.5">Transactions</h3>
				<p class="text-tiny text-surface-7 mb-0">Every payment and refund for this paywall.</p>
			</div>
			<FormButton type="secondary" size="sm" class="shrink-0" :disabled="!filteredTransactions.length"
				@click="onBtnExport">
				<icon-base fill="none" class="!mr-1.5">
					<icon-download />
				</icon-base>
				Export CSV
			</FormButton>
		</div>

		<!-- Search -->
		<div v-if="hasTransactions" class="flex items-center mb-3 relative max-w-xs">
			<icon-base class="absolute z-10 left-2 text-surface-7">
				<icon-search />
			</icon-base>
			<FormInput placeholder="Search by customer" customClasses="!px-8" v-model="search" />
			<FormButton class="absolute z-10 right-0" isIcon type="link" v-if="search" @click="clearSearch">
				<icon-base>
					<icon-cross />
				</icon-base>
			</FormButton>
		</div>

		<!-- Loading -->
		<div v-if="loading" class="flex items-center justify-center min-h-[160px]">
			<Spinner spinner-color="var(--c-dark-8)" />
		</div>

		<!-- Empty: no transactions at all -->
		<div v-else-if="!hasTransactions"
			class="flex flex-col items-center justify-center text-center gap-2 rounded-lg border border-dashed border-surface-3 px-6 py-10">
			<div class="flex size-11 items-center justify-center rounded-full bg-surface-3 text-surface-6">
			<icon-base :width="22" :height="22" :width-view-box="24" :height-view-box="24" fill="none">
				<icon-inbox-empty />
			</icon-base>
			</div>
			<p class="text-sm font-medium text-surface-8 mb-0">No transactions yet</p>
			<p class="text-tiny text-surface-6 mb-0 max-w-sm">Payments and refunds will appear here once viewers start
				purchasing access.</p>
		</div>

		<!-- Table -->
		<template v-else>
			<div class="overflow-x-auto">
				<table class="w-full min-w-[720px] border-separate border-spacing-y-1 text-left">
					<thead>
						<tr class="text-[11px] uppercase tracking-wider text-surface-6">
							<th class="font-semibold px-4 pb-1 w-12">#</th>
							<th class="font-semibold px-4 pb-1">Date</th>
							<th class="font-semibold px-4 pb-1">Customer</th>
							<th class="font-semibold px-4 pb-1 w-32">Amount</th>
							<th class="font-semibold px-4 pb-1 w-32">Status</th>
							<th class="font-semibold px-4 pb-1 w-20 text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!filteredTransactions.length">
							<td colspan="6" class="px-4 py-8 text-center text-tiny text-surface-6">
								No results for “{{ search }}”.
							</td>
						</tr>
						<tr v-for="(row, idx) in pagedTransactions" :key="row.id" class="group">
							<!-- # -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors rounded-l-lg"
								:class="zebraBg(idx)">
								<span class="text-tiny text-surface-6 tabular-nums">{{ rangeStart + idx }}</span>
							</td>
							<!-- Date -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<span class="text-sm text-surface-8 text-nowrap">{{ row.date }}</span>
							</td>
							<!-- Customer -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<div class="flex items-center gap-1.5 min-w-0">
									<span class="text-sm text-surface-text-1 truncate" :title="row.customer">{{ row.customer }}</span>
									<ButtonCopyToClipboard
										class="shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
										:text="row.customer" />
								</div>
							</td>
							<!-- Amount -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<span class="text-sm font-medium text-surface-text-1 text-nowrap">{{ (row.currency || '').toUpperCase() }}
									{{ row.amount }}</span>
							</td>
							<!-- Status -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors" :class="zebraBg(idx)">
								<span
									class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium leading-none"
									:class="[statusMeta(row.status).bg, statusMeta(row.status).text]">
									<span class="size-1.5 rounded-full" :class="statusMeta(row.status).dot"></span>
									{{ statusMeta(row.status).label }}
								</span>
							</td>
							<!-- Actions -->
							<td class="px-4 py-2.5 align-middle group-hover:bg-surface-2 transition-colors rounded-r-lg"
								:class="zebraBg(idx)">
								<div class="flex items-center justify-end gap-1.5">
									<Tooltip wrapperClass="leading-none" content="View details">
										<FormButton type="secondary" isIcon size="sm" @click="openTransactionDetailsModal(row)">
											<icon-base fill="transparent">
												<icon-info />
											</icon-base>
										</FormButton>
									</Tooltip>
									<Tooltip v-if="canRefund(row)" wrapperClass="leading-none" content="Refund">
										<FormButton type="secondary" isIcon size="sm" :disabled="refundingId === row.id"
											@click="refundPayment(row)">
											<icon-base fill="transparent">
												<icon-refund />
											</icon-base>
										</FormButton>
									</Tooltip>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="flex flex-wrap items-center justify-between gap-3 mt-3">
				<p class="text-tiny text-surface-6 mb-0">
					<template v-if="filteredTransactions.length">Showing {{ rangeStart }}–{{ rangeEnd }} of
						{{ filteredTransactions.length }}
						{{ filteredTransactions.length === 1 ? 'transaction' : 'transactions' }}</template>
				</p>
				<div v-if="totalPages > 1" class="flex items-center gap-2">
					<FormButton type="secondary" size="sm" :disabled="page <= 1" @click="page--">Prev</FormButton>
					<span class="text-tiny text-surface-7 tabular-nums">Page {{ page }} of {{ totalPages }}</span>
					<FormButton type="secondary" size="sm" :disabled="page >= totalPages" @click="page++">Next</FormButton>
				</div>
			</div>
		</template>

		<paywall-transaction-details-modal v-if="selectedTxn" :txnObject="selectedTxn" />
	</section>
</template>

<style scoped></style>
